import type { QuizDefaults, QuizRetryPolicy } from "./quiz";
import type { RuntimeId } from "./cells";

// Course manifest types. Emitted by scripts/build-content.ts; consumed by
// the SPA via useCourseManifest. Shape is intentionally minimal — full
// lesson bodies are fetched separately on demand.

export type LessonEnvironment = "browser" | "local" | "setup";

// Moved verbatim from ./video/types (which re-exports it from here) — that
// module also pulls in react + @/lib/telemetry, which this framework-free
// model must never transitively depend on.
export type VideoProviderId = "youtube" | "vimeo" | "url" | "cloudflare-stream";

export interface LessonManifestEntry {
  slug: string;              // e.g. "01-hello-python"
  title: string;
  estimatedMinutes: number;
  environment: LessonEnvironment;
  interactiveRuntimes: RuntimeId[];
  hasVideo?: boolean;
  videoCount?: number;
  videoProviders?: VideoProviderId[];
}

export interface ChapterManifestEntry {
  folder: string;            // e.g. "01-your-first-program"
  chapterNumber: number;     // can be a decimal (5.5 for interstitials)
  title: string;
  environment: LessonEnvironment;
  summary: string;
  learningObjectives: string[];
  lessons: LessonManifestEntry[];
  // Paths to project sibling data files (non-.py) relative to the chapter
  // folder, e.g. "data/deck.txt". The SPA pre-mounts these into Pyodide's
  // virtual FS so project code can open() them.
  projectDataFiles: string[];
  // Overrides the course-level quiz_defaults for all quizzes in this chapter.
  // Absent when the chapter.yaml has no quiz_defaults block.
  quizDefaults?: QuizDefaults;
  // Present when the chapter directory contains a chapter-quiz.md file.
  // The slug is always "chapter-quiz"; title comes from that file's frontmatter.
  chapterQuiz?: { slug: "chapter-quiz"; title: string };
  // Completion celebration content. Absent when chapter.yaml has no completion block.
  completion?: ChapterCompletionBlock;
}

// Per-course navigation gating.
//   "linear" — must complete the current lesson before the next unlocks
//   "open"   — all lessons clickable, free navigation
export type NavigationPolicy = "linear" | "open";

// Chapter-level completion block — authored in chapter.yaml.
// Simpler than the course-level CompletionBlock (no whatYouBuilt, no whatsNext).
export interface ChapterCompletionBlock {
  headline: string;
  whatYouLearned: string[];
  signatureSnippet?: string;  // raw Python (or other lang) — rendered as fenced code block
  wordsToKnow?: string[];     // small pill list, 4-8 items
  nextPreview?: string;       // one-sentence teaser for the next chapter
}

// Completion block types — authored in course.yaml, emitted by build-content.
export interface WhatsNextCourseEntry {
  course: string;   // slug; resolved to title/URL at render time
  pitch: string;
}
export interface WhatsNextExternalEntry {
  url: string;
  label: string;
  pitch: string;
}
export type WhatsNextEntry = WhatsNextCourseEntry | WhatsNextExternalEntry;

export interface CompletionBlock {
  headline: string;
  whatYouLearned: string[];
  whatYouBuilt?: string;
  whatsNext?: WhatsNextEntry[];
}

export type CertificateCriterion =
  | "lessons_complete"
  | "final_exam_passed"
  | "chapter_quizzes_passed";

export interface CertificateConfig {
  enabled: boolean;
  criteria: CertificateCriterion[];
  display: { showHours: boolean; showCompletionDate: boolean };
  verify: "public" | "off";
}

export interface FinalExamManifest {
  title: string;
  source: "dedicated" | "chapter-quizzes";
  passPercent: number;          // 1-100 (§18.4 rename — NOT absolute count)
  retryPolicy: QuizRetryPolicy;
  intro?: string;               // markdown preamble
  totalQuestions: number;       // computed at build time
}

export interface CourseManifest {
  slug: string;              // e.g. "python-for-absolute-beginners"
  title: string;
  audience: string;
  summary: string;
  estimatedHours: number;
  chaptersTotal: number;
  author: string;
  navigationPolicy: NavigationPolicy;
  // True when course.yaml sets `require_onboarding: true`. Always present in
  // the static manifest (defaults to false). The runtime manifest endpoint
  // augments responses with a per-user `onboardingComplete` boolean.
  requireOnboarding: boolean;
  // Per-user gate flag injected by GET /api/courses/:slug/manifest. Absent
  // in the static asset on disk; present in API responses. True when
  // `requireOnboarding` is false or when the user has submitted onboarding.
  onboardingComplete?: boolean;
  // Default quiz behavior for all quizzes in this course. Absent when the
  // course.yaml has no quiz_defaults block.
  quizDefaults?: QuizDefaults;
  // Completion celebration content. Absent when course.yaml has no completion block.
  completion?: CompletionBlock;
  // Final exam configuration. Absent when course.yaml has no final_exam block.
  finalExam?: FinalExamManifest;
  // Certificate configuration. Absent when course.yaml has no certificate block.
  certificate?: CertificateConfig;
  chapters: ChapterManifestEntry[];
}

export interface CourseSummary {
  slug: string;
  title: string;
  videoProviders?: VideoProviderId[];
}

// Per-lesson body is fetched separately as raw MD.
// path: /course/<slug>/<chapter-folder>/<lesson-slug>.md
export function lessonMdPath(
  courseSlug: string,
  chapterFolder: string,
  lessonSlug: string
): string {
  return `/course/${courseSlug}/${chapterFolder}/${lessonSlug}.md`;
}

export function findChapter(
  manifest: CourseManifest,
  folder: string
): ChapterManifestEntry | undefined {
  return manifest.chapters.find((c) => c.folder === folder);
}

export function findLesson(
  chapter: ChapterManifestEntry,
  lessonSlug: string
): LessonManifestEntry | undefined {
  return chapter.lessons.find((l) => l.slug === lessonSlug);
}

// Linear traversal across the whole course. Used by Prev/Next navigation.
export interface LessonLocator {
  chapterFolder: string;
  lessonSlug: string;
}

export function lessonSequence(manifest: CourseManifest): LessonLocator[] {
  const out: LessonLocator[] = [];
  for (const chapter of manifest.chapters) {
    for (const lesson of chapter.lessons) {
      out.push({ chapterFolder: chapter.folder, lessonSlug: lesson.slug });
    }
  }
  return out;
}

/**
 * A flat, ordered sequence that includes real lessons AND chapter-quiz
 * pseudo-lessons. Used by the linear-navigation unlock logic so that chapter
 * quizzes gate the next chapter when present.
 */
export interface SequenceItem {
  chapterFolder: string;
  lessonSlug: string;  // real slug or "chapter-quiz"
  isChapterQuiz: boolean;
}

export function buildFullSequence(manifest: CourseManifest): SequenceItem[] {
  const out: SequenceItem[] = [];
  for (const chapter of manifest.chapters) {
    for (const lesson of chapter.lessons) {
      out.push({ chapterFolder: chapter.folder, lessonSlug: lesson.slug, isChapterQuiz: false });
    }
    if (chapter.chapterQuiz) {
      out.push({ chapterFolder: chapter.folder, lessonSlug: "chapter-quiz", isChapterQuiz: true });
    }
  }
  return out;
}

export function adjacentLessons(
  manifest: CourseManifest,
  current: LessonLocator
): { prev: LessonLocator | null; next: LessonLocator | null } {
  const seq = lessonSequence(manifest);
  const idx = seq.findIndex(
    (l) => l.chapterFolder === current.chapterFolder && l.lessonSlug === current.lessonSlug
  );
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? seq[idx - 1] ?? null : null,
    next: idx < seq.length - 1 ? seq[idx + 1] ?? null : null
  };
}
